import { useState } from 'react';
import { Avatar, Box, Button, Select, Text, TextInput } from 'grommet';
import { getLeagueTeams } from './espnReader';
import { addConnection } from './connections';

// Fall back to location + nickname for older ESPN seasons that don't return `name`.
const teamDisplayName = (t) =>
  t.name || [t.location, t.nickname].filter(Boolean).join(' ') || `Team ${t.id}`;

export default function ConnectLeagueForm({ season, onAdded }) {
  const [leagueId, setLeagueId] = useState('');
  const [teams, setTeams] = useState(null);
  const [leagueName, setLeagueName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [palName, setPalName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | finding | found | saving | error
  const [error, setError] = useState('');

  const findTeams = async () => {
    const id = leagueId.trim();
    if (!id) return;
    setStatus('finding');
    setError('');
    setTeams(null);
    setSelectedTeam(null);
    const result = await getLeagueTeams(id, season);
    if (!result) {
      setStatus('error');
      setError('League not found or not public. Double-check the ID and that the league is public.');
      return;
    }
    setTeams(result.teams);
    setLeagueName(result.leagueName);
    setStatus('found');
  };

  const save = async () => {
    if (!selectedTeam || !palName.trim()) return;
    setStatus('saving');
    setError('');
    try {
      await addConnection({
        palName: palName.trim(),
        leagueId: leagueId.trim(),
        leagueName,
        teamId: selectedTeam.id,
        teamName: teamDisplayName(selectedTeam),
        teamLogo: selectedTeam.logo || '',
        season,
      });
      // Reset for the next add.
      setLeagueId('');
      setTeams(null);
      setLeagueName('');
      setSelectedTeam(null);
      setPalName('');
      setStatus('idle');
      if (onAdded) onAdded();
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Could not save — this team may already be connected.');
    }
  };

  return (
    <Box gap="small" pad="medium" border={{ color: 'brand', size: 'small' }} round="small">
      <Text weight="bold">Add your team</Text>

      <Box direction="row" gap="small" align="center">
        <TextInput
          placeholder="Public ESPN league ID"
          value={leagueId}
          onChange={(e) => setLeagueId(e.target.value)}
        />
        <Button
          label="Find teams"
          onClick={findTeams}
          disabled={status === 'finding' || !leagueId.trim()}
        />
      </Box>

      {status === 'finding' && <Text>Looking up teams…</Text>}
      {error && <Text color="status-critical">{error}</Text>}

      {teams && (
        <Box gap="small">
          {leagueName && <Text size="small">League: {leagueName}</Text>}
          <Select
            placeholder="Select your team"
            options={teams}
            value={selectedTeam || undefined}
            onChange={({ option }) => setSelectedTeam(option)}
            labelKey={teamDisplayName}
            valueKey="id"
          >
            {(option) => (
              <Box direction="row" gap="small" align="center" pad="xsmall">
                <Avatar size="small" src={option.logo || undefined} />
                <Text>{teamDisplayName(option)}</Text>
              </Box>
            )}
          </Select>
          <TextInput
            placeholder="Your name (e.g. Josh)"
            value={palName}
            onChange={(e) => setPalName(e.target.value)}
          />
          <Button
            primary
            label={status === 'saving' ? 'Adding…' : 'Add to Fantasy Pals'}
            onClick={save}
            disabled={status === 'saving' || !selectedTeam || !palName.trim()}
          />
        </Box>
      )}
    </Box>
  );
}
